import { FC } from 'react'
import {
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  Theme,
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { FormattedMessage } from 'react-intl'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import messages from '@src/utils/messages'
import clsx from 'clsx'

/**
 * Returns a banner to provide extra info for quotes.
 * @param {boolean} inlineLink If provided, renders the action inline, otherwise when not present or false
 * will render the link bellow the body message.
 */
interface InfoBannerProps {
  inlineLink?: boolean
  onLearnMoreClick?: (linkUrl: string) => void
}

const useStyles = makeStyles(
  ({
    breakpoints,
    spacing,
    palette: {
      background: { grey },
    },
  }: Theme) => ({
    infoBanner: {
      padding: spacing(6),
      backgroundColor: grey,
    },
    infoBannerSm: {
      marginTop: spacing(8),
      [breakpoints.up('sm')]: {
        marginTop: spacing(10),
      },
    },
    header: {
      paddingBottom: spacing(4),
    },
    getAQuoteInline: {
      width: '100%',
    },
    getAQuote: {
      padding: spacing(6),
      backgroundColor: grey,
      width: '100%',
      marginTop: spacing(10),
    },
    message: {
      marginTop: spacing(1),
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'row',
      gap: spacing(1),
      '& > *': {
        flex: '0 1 auto',
      },
    },
    learnMoreBelow: {
      paddingTop: spacing(6),
    },
    learnMoreInline: {
      marginRight: spacing(1),
    },
  })
)

export const InfoBanner: FC<InfoBannerProps> = ({
  inlineLink = false,
  onLearnMoreClick,
}) => {
  // Hook declarations.
  const classes = useStyles()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { userIsLoggedIn } = useCurrentUser()

  // When true, should render the "Learn more" link inline.
  const shouldLinkInline = isDesktop && inlineLink
  const wrapperClasses = clsx(
    !inlineLink && classes.infoBannerSm,
    classes.infoBanner
  )
  const learnMoreLinkId = 'quick_order_learn_more_link'
  const learnMoreHref =
    'https://maestro.my.site.com/knowledgeportal/s/article/How-do-I-create-a-Quote-also-called-Proforma-Invoice'

  // Don't render component at all, if user is logged.
  if (userIsLoggedIn) return null

  return (
    <Paper variant="outlined" className={wrapperClasses}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={'auto'}>
          <Typography variant={'h3'}>
            <FormattedMessage {...messages.NEED_A_QUOTE} />
          </Typography>
          <Typography variant={'body2'} className={classes.message}>
            {!shouldLinkInline && (
              <FormattedMessage
                id="QUOTES_REQUIRE_ACCOUNT"
                defaultMessage="Quotes require a user account."
              />
            )}
            {shouldLinkInline && (
              <>
                <span className={classes.learnMoreInline}>
                  <FormattedMessage
                    id="QUOTES_REQUIRE_ACCOUNT"
                    defaultMessage="Quotes require a user account."
                  />
                </span>
                <span>
                  <a
                    data-testid={learnMoreLinkId}
                    id={learnMoreLinkId}
                    onClick={
                      onLearnMoreClick &&
                      (() => onLearnMoreClick(learnMoreHref))
                    }
                    href={learnMoreHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FormattedMessage {...messages.LEARN_MORE} />
                  </a>
                </span>
              </>
            )}
          </Typography>
        </Grid>
      </Grid>
      {!shouldLinkInline && (
        <Grid item xs={12} className={classes.learnMoreBelow}>
          <div>
            <a
              data-testid={learnMoreLinkId}
              id={learnMoreLinkId}
              onClick={() =>
                onLearnMoreClick && onLearnMoreClick(learnMoreHref)
              }
              href={learnMoreHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage {...messages.LEARN_MORE} />
            </a>
          </div>
        </Grid>
      )}
    </Paper>
  )
}
